import { useTranslation } from "react-i18next";
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import UndoIcon from "@material-ui/icons/Undo";
import { FieldDef, FieldState } from "../typeConfig";

type Props = {
  field: FieldDef;
  currentVal: string | boolean | undefined;
  defaultVal: string;
  savedVal: string | undefined;
  isEdit: boolean;
  submitted: boolean;
  onStateChange: (state: FieldState) => void;
  onChange: (value: string) => void;
};

export default function FieldCard({
  field,
  currentVal,
  defaultVal,
  savedVal,
  isEdit,
  submitted,
  onStateChange,
  onChange,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation("storage");
  const isLayout = field.kind === "layout";
  const fieldState = isLayout ? "custom" : resolveFieldState(currentVal);
  const isEditable = fieldState === "custom";
  const stringValue = typeof currentVal === "string" ? currentVal : "";
  const displayVal = isEditable ? stringValue : defaultVal;

  const shouldValidate = submitted && isEditable;
  const emptyError = shouldValidate && !isLayout && !stringValue.trim();
  const maxLengthError = shouldValidate && stringValue.length > field.maxLength;
  const hasError = emptyError || maxLengthError;
  const helperText = resolveHelperText(
    emptyError,
    maxLengthError,
    t,
    field.maxLength
  );

  const canResetToSaved =
    isEdit && isEditable && savedVal !== undefined && displayVal !== savedVal;
  const resetTooltip = canResetToSaved
    ? t("notificationTemplates.form.resetToSaved")
    : "";
  const resetVisibility = canResetToSaved ? "visible" : "hidden";

  const titleColor = isEditable ? "textPrimary" : "textSecondary";
  const titleLabel = t(`notificationTemplates.form.field.${field.key}`);
  const minRows = isLayout ? 3 : 1;
  const defaultBtnProps = resolveButtonProps(fieldState, "default");
  const customBtnProps = resolveButtonProps(fieldState, "custom");
  const offBtnProps = resolveButtonProps(fieldState, "off");
  const defaultLabel = t("notificationTemplates.form.stateDefault");
  const customLabel = t("notificationTemplates.form.stateCustom");
  const offLabel = t("notificationTemplates.form.stateOff");

  return (
    <Card variant="outlined" className={classes.card}>
      <CardHeader
        className={classes.header}
        title={
          <Typography variant="subtitle2" color={titleColor}>
            {titleLabel}
          </Typography>
        }
        action={
          <div className={classes.actions}>
            {isEdit && (
              <Tooltip title={resetTooltip}>
                <IconButton
                  size="small"
                  onClick={() => onChange(savedVal!)}
                  disabled={!canResetToSaved}
                  style={{ visibility: resetVisibility }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isLayout && (
              <ButtonGroup size="small" className={classes.stateGroup}>
                <Button
                  variant={defaultBtnProps.variant}
                  color={defaultBtnProps.color}
                  disableElevation
                  onClick={() => onStateChange("default")}
                  className={classes.stateBtn}
                >
                  {defaultLabel}
                </Button>
                <Button
                  variant={customBtnProps.variant}
                  color={customBtnProps.color}
                  disableElevation
                  onClick={() => onStateChange("custom")}
                  className={classes.stateBtn}
                >
                  {customLabel}
                </Button>
                {field.sectionKey && (
                  <Button
                    variant={offBtnProps.variant}
                    color={offBtnProps.color}
                    disableElevation
                    onClick={() => onStateChange("off")}
                    className={classes.stateBtn}
                  >
                    {offLabel}
                  </Button>
                )}
              </ButtonGroup>
            )}
          </div>
        }
      />
      <CardContent className={classes.content}>
        <TextField
          name={field.key}
          variant="outlined"
          size="small"
          fullWidth
          disabled={!isEditable}
          multiline
          minRows={minRows}
          value={displayVal}
          error={hasError}
          helperText={helperText}
          onChange={e => onChange(e.target.value)}
          inputProps={{ maxLength: field.maxLength }}
        />
      </CardContent>
    </Card>
  );
}

function resolveFieldState(
  currentVal: string | boolean | undefined
): FieldState {
  if (currentVal === false) return "off";
  if (typeof currentVal === "string") return "custom";
  return "default";
}

function resolveHelperText(
  emptyError: boolean,
  maxLengthError: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  maxLength: number
): string {
  if (emptyError) return t("notificationTemplates.form.required");
  if (maxLengthError) {
    return t("notificationTemplates.form.maxLength", { max: maxLength });
  }
  return "";
}

function resolveButtonProps(
  state: FieldState,
  target: FieldState
): {
  variant: "contained" | "outlined";
  color: "primary" | "secondary" | "default";
} {
  const isActive = state === target;
  const variant = isActive ? "contained" : "outlined";

  if (!isActive) return { variant, color: "default" };
  if (target === "off") return { variant, color: "secondary" };
  return { variant, color: "primary" };
}

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 8,
    borderColor: theme.palette.divider,
    height: "100%",
  },
  header: {
    padding: "12px 16px 12px 16px",
    "& .MuiCardHeader-action": {
      marginTop: 0,
      marginRight: 0,
      alignSelf: "center",
    },
  },
  content: {
    padding: "0 16px 16px",
    "&:last-child": { paddingBottom: 16 },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  stateGroup: {
    marginLeft: 4,
  },
  stateBtn: {
    padding: "2px 8px",
    fontSize: "0.7rem",
    textTransform: "none",
  },
}));
